import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FIELD_META,
  OVERRIDE_MODES,
  type AgentPromptField,
  type OverrideMode,
} from '@cartcloser/prompts';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Textarea,
} from '@cartcloser/ui';
import {
  getAgent,
  getAgentScope,
  listCategories,
  listProducts,
  setAgentOverride,
  setAgentScope,
  updateAgent,
  type AgentDetail,
  type OverrideMode as ApiOverrideMode,
} from '../lib/api.js';

export const Route = createFileRoute('/admin/$slug/agents/$agentId')({
  component: AgentEditor,
});

function AgentEditor() {
  const { slug, agentId } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['agent', slug, agentId],
    queryFn: () => getAgent(slug, agentId),
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (query.isError || !query.data)
    return (
      <Alert variant="destructive">
        <AlertDescription>Couldn’t load this agent.</AlertDescription>
      </Alert>
    );

  return (
    <EditorBody
      // Key by agentId so navigating editor→editor remounts and re-seeds all
      // local form state instead of showing the previous agent's values.
      key={agentId}
      slug={slug}
      agentId={agentId}
      data={query.data}
      onSaved={() => {
        queryClient.invalidateQueries({ queryKey: ['agent', slug, agentId] });
        queryClient.invalidateQueries({ queryKey: ['agents', slug] });
      }}
    />
  );
}

function EditorBody({
  slug,
  agentId,
  data,
  onSaved,
}: {
  slug: string;
  agentId: string;
  data: AgentDetail;
  onSaved: () => void;
}) {
  const [expertise, setExpertise] = useState(data.agent.expertise);
  const [persona, setPersona] = useState<string>(data.agent.personaArchetype ?? '');
  const [enabled, setEnabled] = useState(data.agent.enabled);
  const [scope, setScope] = useState(data.agent.skuScopeMode);
  // One suggested quick-reply per line in the textarea.
  const [prompts, setPrompts] = useState(data.agent.suggestedPrompts.join('\n'));

  const saveSettings = useMutation({
    mutationFn: () =>
      updateAgent(slug, agentId, {
        expertise,
        personaArchetype: persona === '' ? null : persona,
        enabled,
        skuScopeMode: scope,
        suggestedPrompts: prompts
          .split('\n')
          .map((p) => p.trim())
          .filter(Boolean)
          .slice(0, 5),
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/admin/$slug/agents"
            params={{ slug }}
            className="text-sm text-primary hover:underline"
          >
            ← Agents
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">{data.agent.expertise}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`text-sm ${data.agent.isUnlinked ? 'text-amber-600' : 'text-muted-foreground'}`}
          >
            {data.agent.isUnlinked ? 'Unlinked' : 'Template-based'}
          </span>
          <Button asChild size="sm" variant="outline">
            <Link to="/admin/$slug/sandbox" search={{ agent: agentId }} params={{ slug }}>
              Test this agent
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
          <CardDescription>How the Receptionist routes and scopes this agent.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            label="Expertise"
            description="Shown to the Receptionist for routing."
            help="The short product category a shopper would name (e.g. “Dishwashers”, “Prams”). The Receptionist routes to this agent when a shopper asks about it — keep it recognisable, not a brand or model."
            required
          >
            {({ id }) => (
              <Input id={id} value={expertise} onChange={(e) => setExpertise(e.target.value)} />
            )}
          </FormField>
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              label="Persona"
              help={
                <>
                  Sets the assistant’s tone. <strong>Concierge</strong> is warm and advisory —
                  suited to premium or high-consideration goods. <strong>Practical</strong> is
                  direct and utility-focused. <strong>Default</strong> uses the template’s built-in
                  voice.
                </>
              }
            >
              {({ id }) => (
                <select
                  id={id}
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Default</option>
                  <option value="concierge">Concierge (luxury)</option>
                  <option value="practical">Practical (utility)</option>
                </select>
              )}
            </FormField>
            <FormField
              label="SKU scope"
              help={
                <>
                  <strong>Categories + blacklist</strong> — the agent recommends everything in the
                  ticked categories, so new SKUs added to those categories are picked up
                  automatically (minus any you blacklist). Best for an evolving range.
                  <br />
                  <br />
                  <strong>SKU whitelist</strong> — the agent recommends only the exact SKUs you
                  tick. Best for an agent scoped to a specific range (e.g. the Samsung Galaxy S26
                  generation).
                </>
              }
            >
              {({ id }) => (
                <select
                  id={id}
                  value={scope}
                  onChange={(e) => setScope(e.target.value as typeof scope)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="categories">Categories + blacklist</option>
                  <option value="whitelist">SKU whitelist</option>
                </select>
              )}
            </FormField>
            <FormField label="Status">
              {({ id }) => (
                <label className="flex h-10 items-center gap-2 text-sm" htmlFor={id}>
                  <input
                    id={id}
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  Enabled
                </label>
              )}
            </FormField>
          </div>
          <FormField
            label="Suggested prompts"
            description="Up to 5 canned quick-replies shown to shoppers as tappable chips. One per line, e.g. “Quiet dishwasher”, “Best for a household of 4”."
            help="These appear as tappable chips once a shopper is routed to this agent. Make each one a realistic, information-rich need; when it contains enough detail, the agent can recommend immediately. Hard constraints that leave no matches are explained back with the impact of loosening them. One per line, max 5."
          >
            {({ id }) => (
              <Textarea
                id={id}
                rows={4}
                value={prompts}
                onChange={(e) => setPrompts(e.target.value)}
                placeholder={
                  'Quiet enough for an open-plan kitchen\nFits a household of 4\nBest value under $800'
                }
              />
            )}
          </FormField>
          <Button loading={saveSettings.isPending} onClick={() => saveSettings.mutate()}>
            Save settings
          </Button>
        </CardContent>
      </Card>

      <ScopeEditor slug={slug} agentId={agentId} mode={scope} />

      <Card>
        <CardHeader>
          <CardTitle>Prompts</CardTitle>
          <CardDescription>
            For each prompt, inherit the template, augment it, or overwrite it. Template wording is
            proprietary and stays hidden — you only manage your changes here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {data.fields.map((f) => (
            <FieldEditor
              key={f.field}
              slug={slug}
              agentId={agentId}
              field={f.field as AgentPromptField}
              initialMode={f.mode}
              initialValue={f.value}
              onSaved={onSaved}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function ScopeEditor({
  slug,
  agentId,
  mode,
}: {
  slug: string;
  agentId: string;
  mode: 'categories' | 'whitelist';
}) {
  const categories = useQuery({
    queryKey: ['categories', slug],
    queryFn: () => listCategories(slug),
  });
  const products = useQuery({ queryKey: ['products', slug], queryFn: () => listProducts(slug) });
  const scopeQuery = useQuery({
    queryKey: ['agent-scope', slug, agentId],
    queryFn: () => getAgentScope(slug, agentId),
  });

  const [cats, setCats] = useState<Set<string>>(new Set());
  const [excl, setExcl] = useState<Set<string>>(new Set());
  const [incl, setIncl] = useState<Set<string>>(new Set());
  const [hydrated, setHydrated] = useState(false);
  const [saved, setSaved] = useState(false);

  if (scopeQuery.data && !hydrated) {
    setCats(new Set(scopeQuery.data.categoryIds));
    setExcl(new Set(scopeQuery.data.exclusionProductIds));
    setIncl(new Set(scopeQuery.data.inclusionProductIds));
    setHydrated(true);
  }

  const toggle = (set: Set<string>, setFn: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFn(next);
    setSaved(false);
  };

  // In categories mode, only products IN the ticked categories can be
  // recommended — so the blacklist only covers those (whitelist mode picks
  // from the whole catalog).
  const inCategory = (p: { categoryId: string | null }) => !!p.categoryId && cats.has(p.categoryId);
  const skuList = (products.data ?? []).filter((p) => mode === 'whitelist' || inCategory(p));
  const inCategoryIds = new Set((products.data ?? []).filter(inCategory).map((p) => p.id));

  const save = useMutation({
    mutationFn: () =>
      setAgentScope(slug, agentId, {
        categoryIds: [...cats],
        // Drop any blacklisted SKUs no longer in a ticked category — they can't
        // be recommended anyway, so keeping them would be meaningless.
        exclusionProductIds: products.data
          ? [...excl].filter((id) => inCategoryIds.has(id))
          : [...excl],
        inclusionProductIds: [...incl],
      }),
    onSuccess: () => setSaved(true),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>SKU scope</CardTitle>
        <CardDescription>
          {mode === 'whitelist'
            ? 'Whitelist mode: this agent recommends only the SKUs you tick.'
            : 'Categories mode: this agent recommends products in the ticked categories, minus any you blacklist.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {mode === 'categories' ? (
          <div>
            <p className="mb-2 text-sm font-medium">Categories</p>
            <div className="flex flex-wrap gap-2">
              {categories.data?.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={cats.has(c.id)}
                    onChange={() => toggle(cats, setCats, c.id)}
                  />
                  {c.name}
                </label>
              ))}
              {categories.data?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No categories yet — import a feed.</p>
              ) : null}
            </div>
          </div>
        ) : null}

        <div>
          <p className="mb-2 text-sm font-medium">
            {mode === 'whitelist' ? 'Allowed SKUs' : 'Blacklisted SKUs'}
          </p>
          <div className="max-h-56 space-y-1 overflow-y-auto">
            {skuList.map((p) => {
              const set = mode === 'whitelist' ? incl : excl;
              const setFn = mode === 'whitelist' ? setIncl : setExcl;
              return (
                <label key={p.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={set.has(p.id)}
                    onChange={() => toggle(set, setFn, p.id)}
                  />
                  <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>
                  {p.title}
                </label>
              );
            })}
            {skuList.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {products.data?.length === 0
                  ? 'No products yet — import a feed.'
                  : mode === 'whitelist'
                    ? 'No products yet — import a feed.'
                    : cats.size === 0
                      ? 'Tick a category above to choose SKUs to blacklist.'
                      : 'No products in the selected categories.'}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
            Save scope
          </Button>
          {saved ? <span className="text-xs text-primary">Saved</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function FieldEditor({
  slug,
  agentId,
  field,
  initialMode,
  initialValue,
  onSaved,
}: {
  slug: string;
  agentId: string;
  field: AgentPromptField;
  initialMode: OverrideMode;
  initialValue: string;
  onSaved: () => void;
}) {
  const meta = FIELD_META[field];
  const [mode, setMode] = useState<OverrideMode>(initialMode);
  const [value, setValue] = useState(initialValue);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSaved(false);
  }, [mode, value]);

  const save = useMutation({
    mutationFn: () => setAgentOverride(slug, agentId, field, mode as ApiOverrideMode, value),
    onSuccess: () => {
      setSaved(true);
      onSaved();
    },
  });

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium">{meta.label}</p>
          <p className="text-xs text-muted-foreground">{meta.hint}</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {OVERRIDE_MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-md px-3 py-2 text-xs capitalize ${
                mode === m
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      {mode !== 'inherit' ? (
        <Textarea
          rows={4}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={
            mode === 'augment'
              ? 'Text appended to the template…'
              : 'Replaces the template entirely…'
          }
        />
      ) : (
        <p className="text-xs italic text-muted-foreground">Using the template as-is.</p>
      )}
      <div className="mt-2 flex items-center gap-3">
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
        {saved ? <span className="text-xs text-primary">Saved</span> : null}
      </div>
    </div>
  );
}
